//src/redux/actions/socialActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl, headersDefault } from "./index";
import { updateUserProperty } from "../../redux/reducer/userSlice";
import { getUserInfo } from "./userActions";
import { axiosGetRead, friendlyReadError } from "../../services/readRequest";
import {
  correctImageOrientation,
  downscaleImageFile,
} from "../../services/functions";
import { getAuthToken } from "../../services/authStorage";

const authHeaders = () => headersDefault();

/**
 * FOLLOW / UNFOLLOW
 * - Public target → follow becomes 'active'
 * - Private target → request becomes 'requested'
 */
export const followUser = createAsyncThunk(
  "social/followUser",
  async (targetUserId, thunk) => {
    const response = await axios.post(
      `${baseUrl}/api/master-club-subscriber/${targetUserId}/follow`,
      {},
      { headers: authHeaders() },
    );

    const state = thunk.getState();
    const viewerId = state.user?.user?.id;
    const isPublic =
      !!state.social?.byId?.[targetUserId]?.profile?.is_public_profile;

    // ✅ refresh MY counts + (optional) my following list
    if (viewerId) {
      thunk.dispatch(getUserProfile(viewerId));
      thunk.dispatch(
        getFollowing({ userId: viewerId, page: 1, limit: 10, search: "" }),
      );
    }

    return {
      userId: targetUserId,
      viewerId,
      isPublic,
      coin_transaction: response.data?.coin_transaction || null,
    };
  },
);

export const unfollowUser = createAsyncThunk(
  "social/unfollowUser",
  async (targetUserId, thunk) => {
    await axios.delete(
      `${baseUrl}/api/master-club-subscriber/${targetUserId}/follow`,
      {
        headers: authHeaders(),
        data: { action: "unfollow" },
      },
    );

    const state = thunk.getState();
    const viewerId = state.user?.user?.id;
    const prevStatus =
      state.social?.byId?.[targetUserId]?.profile?.following_status || "none";

    // ✅ refresh MY counts + my following list
    if (viewerId) {
      thunk.dispatch(getUserProfile(viewerId));
      thunk.dispatch(
        getFollowing({ userId: viewerId, page: 1, limit: 10, search: "" }),
      );
    }

    return { userId: targetUserId, viewerId, prevStatus };
  },
);

/** OWNER actions on followers */
export const removeFollower = createAsyncThunk(
  "social/removeFollower",
  async (userId, thunk) => {
    await axios.delete(
      `${baseUrl}/api/master-club-subscriber/${userId}/follow`,
      {
        headers: authHeaders(),
        data: { action: "remove_follower" },
      },
    );
    const ownerId = thunk.getState().user?.user?.id; // me
    return { followerId: userId, ownerId };
  },
);

export const acceptFollow = createAsyncThunk(
  "social/acceptFollow",
  async (userId, thunk) => {
    await axios.post(
      `${baseUrl}/api/master-club-subscriber/${userId}/accept-follow`,
      {},
      { headers: authHeaders() },
    );
    const ownerId = thunk.getState().user?.user?.id; // me
    return { followerId: userId, ownerId };
  },
);

/** LISTS */
// GET followers with search and pagination
export const getFollowers = createAsyncThunk(
  "social/getFollowers",
  async ({ userId, page = 1, limit = 10, search = "" }) => {
    const { data } = await axiosGetRead(
      `${baseUrl}/api/master-club-subscriber/${userId}/followers`,
      {
        params: { page, limit, search },
        headers: authHeaders(),
      },
    );

    // Handle different API response formats
    let followersData = [];
    let paginationData = {};

    if (Array.isArray(data)) {
      followersData = data;
      paginationData = {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: data.length,
        hasMore: false,
      };
    } else if (data?.data && Array.isArray(data.data)) {
      followersData = data.data;
      paginationData = data.pagination || {};
    } else if (data?.followers && Array.isArray(data.followers)) {
      followersData = data.followers;
      paginationData = data.pagination || {};
    } else if (data?.items && Array.isArray(data.items)) {
      followersData = data.items;
      paginationData = data.pagination || {};
    }

    return {
      userId,
      data: followersData,
      pagination: paginationData,
      page,
      search,
    };
  },
);

// GET following with search and pagination
export const getFollowing = createAsyncThunk(
  "social/getFollowing",
  async ({ userId, page = 1, limit = 10, search = "" }) => {
    const { data } = await axiosGetRead(
      `${baseUrl}/api/master-club-subscriber/${userId}/following`,
      {
        params: { page, limit, search },
        headers: authHeaders(),
      },
    );

    console.log("getFollowing API response:", data); // Debug log

    // Handle different API response formats
    let followingData = [];
    let paginationData = {};

    if (Array.isArray(data)) {
      followingData = data;
      paginationData = {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: data.length,
        hasMore: false,
      };
    } else if (data?.data && Array.isArray(data.data)) {
      followingData = data.data;
      paginationData = data.pagination || {};
    } else if (data?.following && Array.isArray(data.following)) {
      followingData = data.following;
      paginationData = data.pagination || {};
    } else if (data?.items && Array.isArray(data.items)) {
      followingData = data.items;
      paginationData = data.pagination || {};
    } else if (data?.followings && Array.isArray(data.followings)) {
      followingData = data.followings;
      paginationData = data.pagination || {};
    }

    // Normalize the data structure - user details are in "following" property
    const normalizedData = followingData.map((item) => {
      // If item has a following property with user details, use it
      if (item.following) {
        return {
          ...item,
          followed: item.following, // Map following to followed for consistency
          user: item.following, // Also add user property for compatibility
        };
      }
      return item;
    });

    return {
      userId,
      data: normalizedData,
      pagination: paginationData,
      page,
      search,
    };
  },
);

export const uploadAvatarAndSave = createAsyncThunk(
  "social/uploadAvatarAndSave",
  async (
    { userId = null, file, avatarNameFromPicker = null, remove = false },
    thunk,
  ) => {
    try {
      const state = thunk.getState();
      const myId = userId ?? state.user?.user?.id; // ✅ robust

      // 1) upload file if needed
      let avatarFilename = avatarNameFromPicker;

      if (!remove && !avatarFilename && file) {
        // Correct orientation + downscale so we upload a small upright JPEG.
        // Raw multi-MB phone photos (often HEIC/rotated) were slow and timed
        // out; fall back to the original on any error.
        let prepared = file;
        try {
          prepared = await downscaleImageFile(
            await correctImageOrientation(file),
            { maxDim: 1024, quality: 0.85 },
          );
        } catch {
          prepared = file;
        }

        const body = new FormData();
        body.append("user-avatars", prepared);

        const { data } = await axios.post(
          `${baseUrl}/api/file-upload/user-avatars`,
          body,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
            timeout: 30000,
          },
        );

        avatarFilename = data?.filename;
      }

      if (!remove && !avatarFilename) {
        throw new Error("Nessuna immagine selezionata");
      }

      // 2) persist avatar on profile. An EMPTY avatar removes it server-side —
      // UserProfileController deletes the stored file and nulls the column when
      // the avatar field is sent empty.
      const avatarValue = remove ? "" : avatarFilename;
      await axios.post(
        `${baseUrl}/api/profilo-utenti`,
        JSON.stringify({ avatar: avatarValue }),
        { headers: headersDefault() },
      );

      // 3) reflect immediately into USER reducer (instant UI)
      thunk.dispatch(
        updateUserProperty({
          stateKey: "user",
          key: "avatar",
          value: remove ? null : avatarFilename,
        }),
      );

      // ✅ 4) HARD refresh in PARALLEL (was 3 sequential awaits → the button
      // stayed in "uploading" far longer than needed).
      await Promise.allSettled([
        thunk.dispatch(getUserInfo()).unwrap(),
        myId ? thunk.dispatch(getUserProfile(myId)).unwrap() : Promise.resolve(),
        myId
          ? thunk.dispatch(getUserSocialProfile(myId)).unwrap()
          : Promise.resolve(),
      ]);

      return { userId: myId, avatar: remove ? null : avatarFilename };
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data || { message: e.message });
    }
  },
);

export const getUserProfile = createAsyncThunk(
  "social/getUserProfile",
  async (userId) => {
    try {
      const { data } = await axiosGetRead(
        `${baseUrl}/api/master-club-subscriber/${userId}/profile`,
        { headers: headersDefault(), timeout: 10000 },
      );
      return { userId, data };
    } catch (err) {
      throw new Error(friendlyReadError(err, "Errore caricamento profilo"));
    }
  },
);

/**
 * Write or clear the signed-in member's profile bio.
 *
 * ⚠️ The endpoint resolves the row from the AUTHENTICATED user, not from the id
 * in the path, so this can only ever write your own — passing someone else's id
 * would not reach their profile.
 *
 * `null` is how a bio is deleted. It is sent explicitly rather than as an empty
 * string so the server stores null and the profile falls back to its empty
 * state, instead of rendering a blank line that looks like a rendering bug.
 */
export const updateMyBio = createAsyncThunk(
  "social/updateMyBio",
  async ({ userId, bio }, thunk) => {
    try {
      const response = await fetch(
        `${baseUrl}/api/v1/master-club-subscribers/${userId}`,
        {
          method: "PATCH",
          headers: headersDefault(),
          body: JSON.stringify({ bio: bio === null ? null : String(bio) }),
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return thunk.rejectWithValue(
          data?.errors?.bio?.[0] ||
            data?.message ||
            "Impossibile salvare la biografia",
        );
      }

      return { userId, bio };
    } catch (e) {
      return thunk.rejectWithValue(e?.message ?? "Errore rete");
    }
  },
);

// GET /api/v1/master-club-social-profile/{userId}
export const getUserSocialProfile = createAsyncThunk(
  "social/getUserSocialProfile",
  async (userId) => {
    try {
      const { data } = await axiosGetRead(
        `${baseUrl}/api/v1/master-club-social-profile/${userId}`,
        { headers: headersDefault() },
      );
      return { userId, data };
    } catch (err) {
      throw new Error(
        friendlyReadError(err, "Errore caricamento social profile"),
      );
    }
  },
);

// src/redux/actions/socialActions.js
export const deletePost = createAsyncThunk(
  "social/deletePost",
  async (postGroupId, thunk) => {
    // NEW endpoint & path param
    await axios.delete(
      `${baseUrl}/api/master-club-post-groups/${postGroupId}`,
      { headers: authHeaders() },
    );
    const myId = thunk.getState().user?.user?.id;
    return { postGroupId, myId };
  },
);

// PUT (own) profile privacy (payload shape as per backend)
export const updateProfilePrivacy = createAsyncThunk(
  "social/updateProfilePrivacy",
  async ({ myUserId, is_public_profile }) => {
    const { data } = await axios.put(
      `${baseUrl}/api/v1/master-club-subscribers/${myUserId}`,
      { is_public_profile },
      { headers: headersDefault() },
    );
    return {
      myUserId,
      is_public_profile: data?.is_public_profile ?? is_public_profile,
    };
  },
);

// GET posts for user (privacy-aware)
export const getUserPosts = createAsyncThunk(
  "social/getUserPosts",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await axiosGetRead(
        `${baseUrl}/api/master-club-subscriber/${userId}/posts`,
        { headers: headersDefault() },
      );
      return { userId, data };
    } catch (err) {
      if (err?.response?.status === 404) {
        // private profile — not allowed
        return rejectWithValue({ userId, private: false });
      } else if (err?.response?.status === 401) {
        return rejectWithValue({ userId, private: true });
      }
      throw new Error(friendlyReadError(err, "Errore caricamento post"));
    }
  },
);

// GET posts for user with pagination (for infinite scroll)
export const getUserPostsPaginated = createAsyncThunk(
  "social/getUserPostsPaginated",
  async ({ userId, page = 1 }, { rejectWithValue }) => {
    try {
      const { data } = await axiosGetRead(
        `${baseUrl}/api/master-club-subscriber/${userId}/posts`,
        { 
          params: { page },
          headers: headersDefault() 
        },
      );
      return { userId, data, page };
    } catch (err) {
      if (err?.response?.status === 404) {
        return rejectWithValue({ userId, private: false });
      } else if (err?.response?.status === 401) {
        return rejectWithValue({ userId, private: true });
      }
      throw new Error(friendlyReadError(err, "Errore caricamento post"));
    }
  },
);
// src/redux/actions/socialActions.js - ADD THESE TO EXISTING FILE

// Get discover users
export const getDiscoverUsers = createAsyncThunk(
  "social/getDiscoverUsers",
  async (
    { userId, page = 1, limit = 10, search = "" },
    { rejectWithValue },
  ) => {
    try {
      console.log(" [getDiscoverUsers] Fetching users for:", {
        userId,
        page,
        limit,
        search,
        url: `${baseUrl}/api/master-club-subscriber/${userId}/discover-users`,
      });

      const response = await axiosGetRead(
        `${baseUrl}/api/master-club-subscriber/${userId}/discover-users`,
        {
          params: { page, limit, search },
          headers: headersDefault(),
        },
      );

      // Try different response structures
      const users =
        response.data?.users ||
        response.data?.data?.users ||
        response.data?.data ||
        response.data ||
        [];

      return {
        userId,
        data: {
          users: users,
          pagination: response.data?.pagination || {
            current_page: page,
            last_page: 1,
            per_page: limit,
            total: users.length,
            hasMore: false,
          },
        },
        page,
        search,
      };
    } catch (error) {
      console.error(" [getDiscoverUsers] Error:", error);
      console.error(" [getDiscoverUsers] Error response:", error.response);
      console.error(" [getDiscoverUsers] Error message:", error.message);
      return rejectWithValue(friendlyReadError(error));
    }
  },
);

// Follow user from discover section
export const followUserFromDiscover = createAsyncThunk(
  "social/followUserFromDiscover",
  async ({ userId, targetUserId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/master-club-subscriber/${targetUserId}/follow`,
        {},
        { headers: headersDefault() },
      );

      // ✅ refresh MY counts (Seguiti)
      dispatch(getUserProfile(userId));
      dispatch(getFollowing({ userId, page: 1, limit: 10, search: "" }));

      return {
        userId,
        targetUserId,
        followingStatus: response.data?.following_status || "requested",
        isPublic: response.data?.is_public || false,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Follow user from followers/following list
export const followUserFromList = createAsyncThunk(
  "social/followUserFromList",
  async ({ targetUserId, listType, viewerId }, thunk) => {
    const response = await axios.post(
      `${baseUrl}/api/master-club-subscriber/${targetUserId}/follow`,
      {},
      { headers: headersDefault() },
    );

    const state = thunk.getState();
    const vid = viewerId ?? state.user?.user?.id;
    if (vid) thunk.dispatch(getUserProfile(vid));
    const isPublic =
      !!state.social?.byId?.[targetUserId]?.profile?.is_public_profile;

    return {
      targetUserId,
      viewerId: vid,
      listType,
      followingStatus: isPublic ? "active" : "requested",
      coin_transaction: response.data?.coin_transaction || null,
    };
  },
);

// Unfollow user from following list
export const unfollowUserFromList = createAsyncThunk(
  "social/unfollowUserFromList",
  async ({ targetUserId, viewerId, listType }, thunk) => {
    await axios.delete(
      `${baseUrl}/api/master-club-subscriber/${targetUserId}/follow`,
      {
        headers: headersDefault(),
        data: { action: "unfollow" },
      },
    );

    const state = thunk.getState();
    const vid = viewerId ?? state.user?.user?.id;
    if (vid) thunk.dispatch(getUserProfile(vid)); // ✅ refresh counts

    return {
      targetUserId,
      viewerId: vid,
      listType,
      followingStatus: "none",
    };
  },
);

// Send message action (placeholder - you'll need to implement your actual messaging)
export const sendMessage = createAsyncThunk(
  "social/sendMessage",
  async ({ targetUserId }) => {
    // This is a placeholder - implement your actual message sending logic
    console.log(`Sending message to user ${targetUserId}`);
    return { targetUserId };
  },
);

// Get Dieta Social user ID (fetches the official Dieta Social profile)
export const getDietaSocialUserId = createAsyncThunk(
  "social/getDietaSocialUserId",
  async (_, { rejectWithValue }) => {
    try {
      // This endpoint should return the Dieta Social user info
      const { data } = await axiosGetRead(
        `${baseUrl}/api/dieta-social-profile`,
        { headers: headersDefault() },
      );
      return { userId: data?.user_id || data?.id, data };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  },
);

/**
 * BLOCK / UNBLOCK (Apple Guideline 1.2)
 * Blocking hides the target user's community posts/comments (backend filter)
 * and, in chat, is enforced Stream-side by the caller.
 */
export const blockUser = createAsyncThunk(
  "social/blockUser",
  async (targetUserId, { rejectWithValue }) => {
    try {
      await axios.post(
        `${baseUrl}/api/v1/master-club-blocks`,
        { blocked_id: targetUserId },
        { headers: authHeaders() },
      );
      return { userId: targetUserId };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  },
);

export const unblockUser = createAsyncThunk(
  "social/unblockUser",
  async (targetUserId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${baseUrl}/api/v1/master-club-blocks/${targetUserId}`,
        { headers: authHeaders() },
      );
      return { userId: targetUserId };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  },
);

export const getBlockedUsers = createAsyncThunk(
  "social/getBlockedUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosGetRead(
        `${baseUrl}/api/v1/master-club-blocks`,
        { headers: headersDefault() },
      );
      return { data: data?.data ?? [] };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  },
);
